import { useParams } from 'react-router';
import Spinner from '~/components/General/Spinner';
import Alert from '~/components/General/Alert';
import { useCustomerTravelplan } from '~/hooks/myEnvironment/useCustomerTravelplan';
import ReservationHeader from '~/components/MyEnvironment/ReservationHeader';
import Participant from '~/components/MyEnvironment/Participant';
import utilStyles from '~/styles/utils.module.css';
import reservationStyles from '~/styles/myEnvironment/reservation.module.css';
import myEnvironmentStyles from '~/styles/myEnvironment/my-environment.module.css';
import useTranslation from '~/hooks/useTranslation';
import cnh from '~/helpers/cnh';
import { useOptions } from '~/hooks/useOptions';
import { InferredMyEnvironmentOptions } from '~/types/options';
import GetInTouch from '~/components/MyEnvironment/GetInTouch';
import OrderBlock from '~/components/MyEnvironment/OrderBlock';
import MyEnvironmentLayout from './Layout';
import ReservationFiles from './ReservationFiles';

export default function Reservation() {
  const { travelplanId } = useParams();
  const options = useOptions() as InferredMyEnvironmentOptions;
  const translate = useTranslation();

  const { data, isFetching, error } = useCustomerTravelplan({
    travelplan_id: travelplanId ?? '',
    locale: options.locale,
    office_id: options.officeId,
    department_id: options.departmentId,
  });

  if (error) {
    return (
      <MyEnvironmentLayout>
        <Alert type="danger" error={error} />
      </MyEnvironmentLayout>
    );
  }

  if (isFetching || !data) {
    return (
      <MyEnvironmentLayout>
        <Spinner />
      </MyEnvironmentLayout>
    );
  }

  const mainBooker = data.participants.find((participant) => participant.main_booker);
  const participants = data.participants.filter((participant) => !participant.main_booker);

  return (
    <MyEnvironmentLayout>
      <ReservationHeader
        className={utilStyles.mbMedium}
        id={data.id}
        name={data.name}
        startDate={data.start_date}
        endDate={data.end_date}
        pax={data.pax}
        guiboReleaseUrl={data.guibo_release_url}
      />

      <section className={cnh(reservationStyles.section, reservationStyles.sectionFirst)}>
        <h2 className={reservationStyles.subheading}>
          {translate('myEnvironment.reservation.participants_title')}
        </h2>

        <div className={reservationStyles.participantsList}>
          {mainBooker && (
            <Participant
              title={translate('myEnvironment.reservation.main_booker_title')}
              firstName={mainBooker.first_name}
              lastName={mainBooker.last_name}
              gender={mainBooker.gender}
              birthDate={mainBooker.birth_date}
              email={mainBooker.email}
              phone={mainBooker.phone}
              address={
                mainBooker.address
                  ? {
                    street: mainBooker.address.street,
                    housenum: mainBooker.address.housenum,
                    addressLine2: mainBooker.address.address_line_2,
                    city: mainBooker.address.city,
                    state: mainBooker.address.state,
                    postalCode: mainBooker.address.postal_code,
                    country: mainBooker.address.country,
                  }
                  : undefined
              }
            />
          )}

          {participants.map((participant, participantIndex) => (
            <Participant
              key={participant.id}
              title={translate('myEnvironment.reservation.traveler_title', { number: participantIndex + 2 })}
              firstName={participant.first_name}
              lastName={participant.last_name}
              gender={participant.gender}
              birthDate={participant.birth_date}
            />
          ))}
        </div>
      </section>

      {data.order_blocks && data.order_blocks.length > 0 && (
        <section className={reservationStyles.section}>
          <h2 className={reservationStyles.subheading}>
            {translate('myEnvironment.reservation.overview_title')}
          </h2>

          <ul className={reservationStyles.accommodationsList}>
            {data.order_blocks.map((orderBlock) => (
              <li key={orderBlock.id}>
                <OrderBlock
                  name={orderBlock.name}
                  startDate={orderBlock.start_date}
                  endDate={orderBlock.end_date}
                  lines={orderBlock.lines}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.is_paid && (
        <section className={reservationStyles.section}>
          <h2 className={reservationStyles.subheading}>
            {translate('myEnvironment.reservation.files_title')}
          </h2>

          <div className={reservationStyles.files}>
            <ReservationFiles
              travelplanId={Number(data.id)}
            />
          </div>
        </section>
      )}

      {options.contactEmail && (
        <GetInTouch
          className={myEnvironmentStyles.getInTouch}
          email={options.contactEmail}
          subject={`${data.id} - ${data.name}`}
        />
      )}
    </MyEnvironmentLayout>
  );
}
